import {
    SAME_SHIPPING_ADDRESS_STORAGE_KEY,
    VERIFIED_SHIPPING_ADDRESS_STORAGE_KEY
} from "@/lib/siteconfig/constants"
import { sanitizeAddress } from "@/lib/utils/checkout/sanitizeAddress"
import { create } from "zustand"
import type { AvailableSizes, ColorSwatch } from "./types"

// --- Product store ---

type AddedProduct = {
    name: string
    size: string
    price: string
    image: string
    color_name: string
}

type ProductStore = {
    colorSwatches: ColorSwatch[]
    selectedColor: string
    availableSizes: AvailableSizes[]
    selectedSize: string
    canAddToCart: boolean
    addedProduct: AddedProduct
    setColorSwatches: (swatches: ColorSwatch[]) => void
    setAvailableSizes: (sizes: AvailableSizes[]) => void
    selectColorSwatch: (color: string) => void
    selectSize: (size: string) => void
    updateCanAddToCart: () => void
    setAddedProduct: (product: AddedProduct) => void
}

export const useProductStore = create<ProductStore>((set, get) => ({
    colorSwatches: [],
    selectedColor: "",
    availableSizes: [],
    selectedSize: "",
    canAddToCart: false,
    addedProduct: { name: "", size: "", price: "", image: "", color_name: "" },

    setColorSwatches: swatches => {
        const normalized = swatches.map(s => ({
            ...s,
            isSelected: s.isSelected ?? false
        }))
        const selected = normalized.find(s => s.isSelected)
        set({ colorSwatches: normalized, selectedColor: selected?.color_name ?? "" })
    },
    setAvailableSizes: sizes => {
        const selected = sizes.find(s => s.isSelected)
        set({ availableSizes: sizes, selectedSize: selected?.name ?? "" })
    },
    selectColorSwatch: color => {
        set(state => ({
            colorSwatches: state.colorSwatches.map(s => ({
                ...s,
                isSelected: s.color_name === color
            })),
            selectedColor: color
        }))
    },
    selectSize: size => {
        set(state => ({
            availableSizes: state.availableSizes.map(s => ({
                ...s,
                isSelected: s.name === size
            })),
            selectedSize: size
        }))
    },
    updateCanAddToCart: () => {
        const { colorSwatches, availableSizes } = get()
        set({
            canAddToCart:
                colorSwatches.some(s => s.isSelected && s.isAvailable) &&
                availableSizes.some(s => s.isSelected && s.isAvailable)
        })
    },
    setAddedProduct: product => set({ addedProduct: product })
}))

// --- Checkout form store ---

export type CheckoutAddress = {
    email?: string
    firstName?: string
    lastName?: string
    address1?: string
    apartmentHouseNumber?: string
    address2?: string
    zipCode?: string
    city?: string
    country?: string
    state?: string
    phoneNumber?: string
}

export type CheckoutFormState = {
    billingAddress: CheckoutAddress
    shippingAddress: CheckoutAddress
}

type CheckoutFormStore = CheckoutFormState & {
    /** The last shipping address Loqate settled; see `needsAddressVerification`. */
    verifiedShippingAddressKey: string | null
    hydrate: (data: CheckoutFormState) => void
    setBillingAddress: (address: CheckoutAddress) => void
    setShippingAddress: (address: CheckoutAddress) => void
    setVerifiedShippingAddressKey: (key: string | null) => void
    clearBillingAddress: () => void
    clearShippingAddress: () => void
}

/** Outlives a page load, so "use as entered" survives a refresh. Scoped to this visit. */
const readVerifiedShippingAddressKey = (): string | null =>
    typeof window === "undefined"
        ? null
        : sessionStorage.getItem(VERIFIED_SHIPPING_ADDRESS_STORAGE_KEY)

const writeVerifiedShippingAddressKey = (key: string | null) => {
    if (typeof window === "undefined") {
        return
    }

    if (key === null) {
        sessionStorage.removeItem(VERIFIED_SHIPPING_ADDRESS_STORAGE_KEY)
        return
    }

    sessionStorage.setItem(VERIFIED_SHIPPING_ADDRESS_STORAGE_KEY, key)
}

export const useCheckoutFormStore = create<CheckoutFormStore>(set => ({
    billingAddress: {},
    shippingAddress: {},
    verifiedShippingAddressKey: readVerifiedShippingAddressKey(),

    hydrate: data => set(state => ({ ...state, ...data })),
    setBillingAddress: address => {
        const sameShipping =
            typeof window !== "undefined" &&
            sessionStorage.getItem(SAME_SHIPPING_ADDRESS_STORAGE_KEY) === "true"
        set(state => ({
            billingAddress: {
                ...state.billingAddress,
                ...sanitizeAddress(address, !sameShipping)
            }
        }))
    },
    setShippingAddress: address =>
        set(state => ({
            shippingAddress: { ...state.shippingAddress, ...sanitizeAddress(address) }
        })),
    setVerifiedShippingAddressKey: key => {
        writeVerifiedShippingAddressKey(key)
        set({ verifiedShippingAddressKey: key })
    },
    clearBillingAddress: () => set({ billingAddress: {} }),
    clearShippingAddress: () => set({ shippingAddress: {} })
}))
